import { useEffect, useState } from "react";
import { marked } from "marked";
import { Link } from "react-router-dom";
import { Home, ChevronDown } from "lucide-react";
import { API_BASE_URL } from "../api";

if (!Array.prototype.at) {
  Array.prototype.at = function (n) {
    n = Math.trunc(n) || 0;
    if (n < 0) n += this.length;
    return n >= 0 && n < this.length ? this[n] : undefined;
  };
}
if (!String.prototype.at) {
  String.prototype.at = function (n) {
    n = Math.trunc(n) || 0;
    if (n < 0) n += this.length;
    return n >= 0 && n < this.length ? this[n] : undefined;
  };
}
/* ─────────────────────────────────────────────────────────────────
   marked config — breaks:false so each "\n\n" paragraph becomes
   its own <p> tag (not <br> inside one <p>)
───────────────────────────────────────────────────────────────── */
marked.setOptions({ breaks: false, gfm: true });

/* ─────────────────────────────────────────────────────────────────
   BULLET DOT SPAN — reused in both postProcess paths
───────────────────────────────────────────────────────────────── */
const DOT =
  `<span style="display:inline-block;min-width:7px;width:7px;height:7px;` +
  `border-radius:50%;background:#22C55E;margin-top:7px;flex-shrink:0;"></span>`;

const LI_STYLE =
  `display:flex;align-items:flex-start;gap:10px;margin-bottom:8px;` +
  `font-size:0.875rem;line-height:1.75;color:#334155;font-weight:400;`;

const UL_STYLE = `list-style:none;padding:0;margin:0 0 8px 0;`;

/* ─────────────────────────────────────────────────────────────────
   postProcess(html)

   Rendering rules:
   1. <p> immediately followed by <ul>/<ol>  →  label paragraph + indented list
   2. <p> not followed by a list             →  standalone gold-dot bullet
   3. Consecutive standalone <p> bullets are grouped into one <ul>
   4. Existing <ul>/<ol> (standalone)        →  restyle with gold dots
   5. <h3>  →  uppercase small-caps style
   6. <h4>  →  smaller uppercase style
   7. Everything else passes through unchanged
───────────────────────────────────────────────────────────────── */
function postProcess(html) {
  // ── tokeniser: pull one block element at a time from the front ──
  const BLOCK_RE =
    /^(<(p|ul|ol|h[1-6]|blockquote|table|pre|div)[\s>][\s\S]*?<\/\2>|<hr\s*\/?>)/i;

  const tokens = [];
  let remaining = html.trim();
  while (remaining.length > 0) {
    remaining = remaining.trimStart();
    if (!remaining) break;
    const m = BLOCK_RE.exec(remaining);
    if (m) {
      tokens.push(m[1]);
      remaining = remaining.slice(m[1].length);
    } else {
      const next = remaining.indexOf("<", 1);
      if (next === -1) {
        tokens.push(remaining);
        break;
      }
      tokens.push(remaining.slice(0, next));
      remaining = remaining.slice(next);
    }
  }

  // ── helpers ──────────────────────────────────────────────────────
  const getTag = (t) => {
    if (!t) return null;
    const m = /^<(p|ul|ol|h[1-6]|blockquote|table|pre|hr)/i.exec(t.trimStart());
    return m ? m[1].toLowerCase() : null;
  };

  const innerOf = (token, tag) =>
    token
      .replace(new RegExp(`^<${tag}[^>]*>`, "i"), "")
      .replace(new RegExp(`<\\/${tag}>$`, "i"), "")
      .trim();

  // Gold-dot top-level list (used for standalone <p> bullet groups only)
  const restyleList = (token, tag) => {
    const openStyle =
      tag === "ol"
        ? `<ol style="padding-left:1.2rem;margin:0 0 8px 0;">`
        : `<ul style="${UL_STYLE}">`;
    return token
      .replace(new RegExp(`^<${tag}[^>]*>`, "i"), openStyle)
      .replace(
        /<li[^>]*>([\s\S]*?)<\/li>/gi,
        (_, inner) =>
          `<li style="${LI_STYLE}">${DOT}<span>${inner.trim()}</span></li>`,
      );
  };

  // Hyphen sub-list style — used for label's <ul> and orphan <ul>/<ol>
  const HYPHEN = `<span style="color:#334155;font-weight:700;margin-top:0px;flex-shrink:0;">—</span>`;
  const SUB_LI_STYLE =
    `display:flex;align-items:flex-start;gap:8px;margin-bottom:6px;` +
    `font-size:0.8125rem;line-height:1.7;color:#334155;font-weight:400;`;

  const restyleSubList = (token, tag) => {
    const openStyle =
      tag === "ol"
        ? `<ol style="list-style:none;padding:0;margin:0 0 8px 0;padding-left:24px;">`
        : `<ul style="list-style:none;padding:0;margin:0 0 8px 0;padding-left:24px;">`;
    return token
      .replace(new RegExp(`^<${tag}[^>]*>`, "i"), openStyle)
      .replace(
        /<li[^>]*>([\s\S]*?)<\/li>/gi,
        (_, inner) =>
          `<li style="${SUB_LI_STYLE}">${HYPHEN}<span>${inner.trim()}</span></li>`,
      );
  };

  // ── main pass ─────────────────────────────────────────────────────
  const result = [];
  let pBuffer = [];

  const flushP = () => {
    if (pBuffer.length === 0) return;
    const items = pBuffer
      .map((t) => `<li style="${LI_STYLE}">${DOT}<span>${t}</span></li>`)
      .join("");
    result.push(`<ul style="${UL_STYLE}">${items}</ul>`);
    pBuffer = [];
  };

  let i = 0;
  while (i < tokens.length) {
    const token = tokens[i];
    const next = tokens[i + 1] ?? null;
    const tag = getTag(token);
    const nextTag = getTag(next);

    if (tag === "p") {
      if (nextTag === "ul" || nextTag === "ol") {
        // ── label bullet + indented hyphen sub-list ──
        flushP();
        const labelText = innerOf(token, "p");
        const labelLi =
          `<ul style="list-style:none;padding:0;margin:0 0 4px 0;">` +
          `<li style="${LI_STYLE}">${DOT}<span>${labelText}</span></li>` +
          `</ul>`;
        const subList = restyleSubList(next, nextTag);
        result.push(
          labelLi +
            `<div style="padding-left:0;margin-bottom:12px;">${subList}</div>`,
        );
        i += 2;
      } else {
        // ── standalone gold-dot bullet ──
        const inner = innerOf(token, "p");
        if (inner) pBuffer.push(inner);
        i++;
      }
      continue;
    }

    if (tag === "ul" || tag === "ol") {
      // ── orphan list → hyphen style ──
      flushP();
      result.push(restyleSubList(token, tag));
      i++;
      continue;
    }

    if (tag === "h3") {
      flushP();
      result.push(
        `<p style="font-size:0.8rem;font-weight:900;color:#0f2d5e;text-transform:uppercase;` +
          `letter-spacing:0.05em;margin:16px 0 8px 0;padding-bottom:4px;border-bottom:1px solid #e2e8f0;">` +
          `${innerOf(token, "h3")}</p>`,
      );
      i++;
      continue;
    }

    if (tag === "h4") {
      flushP();
      result.push(
        `<p style="font-size:0.75rem;font-weight:700;color:#475569;text-transform:uppercase;` +
          `letter-spacing:0.04em;margin:12px 0 6px 0;">` +
          `${innerOf(token, "h4")}</p>`,
      );
      i++;
      continue;
    }

    // blockquote, table, pre, hr, other — pass through
    flushP();
    result.push(token);
    i++;
  }

  flushP();
  return result.join("\n");
}

/* ─────────────────────────────────────────────────────────────────
   Main page component
───────────────────────────────────────────────────────────────── */
export default function TermsAndConditionsPage() {
  const [sections, setSections] = useState([]);
  const [title, setTitle] = useState("Terms & Conditions");
  const [lastUpdated, setLastUpdated] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openSections, setOpenSections] = useState({});

  useEffect(() => {
    async function fetchTerms() {
      try {
        setLoading(true);
        const res = await fetch(
          `${API_BASE_URL}/policies/terms-and-conditions`,
          {
            credentials: "include",
          },
        );
        if (!res.ok) throw new Error(`Server responded with ${res.status}`);

        const data = await res.json();

        if (data?.title) setTitle(data.title);
        setLastUpdated(
          data?.last_updated ||
            new Date().toLocaleDateString("en-US", {
              month: "long",
              year: "numeric",
            }),
        );

        if (!Array.isArray(data?.sections) || data.sections.length === 0)
          throw new Error("No content found in API response");

        const parsed = data.sections.map((s) => ({
          ...s,
          html: postProcess(marked.parse(s.body_markdown || "")),
        }));

        setSections(parsed);
        // All open by default
        setOpenSections(
          Object.fromEntries(parsed.map((s) => [s.anchor, true])),
        );
      } catch (err) {
        console.error("[TermsPage]", err);
        setError("We're having trouble loading this page. Please try again in a moment.");
      } finally {
        setLoading(false);
      }
    }
    fetchTerms();
  }, []);

  useEffect(() => {
    document.title = `${title} | RentGurus`;
  }, [title]);

  const toggleSection = (anchor) =>
    setOpenSections((prev) => ({ ...prev, [anchor]: !prev[anchor] }));

  const allOpen =
    sections.length > 0 &&
    sections.every((s) => openSections[s.anchor] !== false);

  const toggleAll = () => {
    const next = !allOpen;
    setOpenSections(Object.fromEntries(sections.map((s) => [s.anchor, next])));
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col mt-16">
      {/* Hero */}
      <div
        className="bg-black text-white w-full pt-14 pb-10 px-4 text-center"
        style={{
          background:
            "linear-gradient(135deg, #14222d 0%, #14222d 45%, #405759 60%, #587471 78%, #90bda8 100%)",
        }}
      >
        <h1
          className="font-heading font-bold uppercase tracking-wide text-white"
          style={{ fontSize: "clamp(1.25rem, 4vw, 1.875rem)" }}
        >
          Terms and Conditions
        </h1>
        <div className="mt-2 w-14 h-1 bg-primary rounded-full mx-auto" />
      </div>

      <div className="max-w-6xl mx-auto w-full px-4 py-8 flex-1">
        {/* Breadcrumb */}

        {/* Loading */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-32 gap-4 text-slate-400">
            <div className="w-8 h-8 border-4 border-slate-200 border-t-primary rounded-full animate-spin" />
            <span className="text-sm font-medium">Loading…</span>
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div className="bg-white rounded-xl border border-slate-200 p-10 text-center shadow-sm">
            <p className="text-red-500 font-semibold mb-3">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="text-sm font-bold text-blue-700 underline hover:no-underline"
            >
              Try again
            </button>
          </div>
        )}

        {/* Sections */}
        {!loading && !error && sections.length > 0 && (
          <>
            <div className="flex items-center justify-between mb-6">
              <nav className="flex items-center gap-1.5 text-sm text-slate-500">
                <Link
                  to="/"
                  className="flex items-center gap-1 hover:text-black transition-colors"
                >
                  <Home size={14} />
                  <span>Home</span>
                </Link>
                <span className="text-slate-300">›</span>
                <span className="font-heading text-primary font-semibold">
                  {title}
                </span>
              </nav>
              <button
                onClick={toggleAll}
                className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 bg-white border border-slate-200 rounded-lg px-3 py-1.5 shadow-sm hover:border-primary hover:text-navy transition-colors"
              >
                <ChevronDown
                  size={13}
                  style={{
                    transform: allOpen ? "rotate(0deg)" : "rotate(-90deg)",
                    transition: "transform 0.2s",
                  }}
                />
                {allOpen ? "Collapse All" : "Expand All"}
              </button>
            </div>
            {/* Collapse / Expand all */}

            <div className="space-y-4">
              {sections.map((s) => {
                const isOpen = openSections[s.anchor] !== false;
                return (
                  <div
                    key={s.anchor}
                    id={s.anchor}
                    className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden"
                    style={{ borderTop: "3px solid #22C55E" }}
                  >
                    {/* Section header */}
                    <button
                      onClick={() => toggleSection(s.anchor)}
                      className="w-full text-left px-6 pt-5 pb-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
                      aria-expanded={isOpen}
                    >
                      <div>
                        <h2 className="font-heading text-base font-black text-slate-800">
                          {s.title}
                        </h2>
                        <div className="mt-1.5 w-8 h-0.5 bg-primary rounded-full" />
                      </div>
                      <ChevronDown
                        size={18}
                        className="text-slate-400 flex-shrink-0 ml-4 transition-transform duration-300"
                        style={{
                          transform: isOpen ? "rotate(0deg)" : "rotate(-90deg)",
                        }}
                      />
                    </button>

                    {/* Collapsible body */}
                    <div
                      style={{
                        maxHeight: isOpen ? "5000px" : "0",
                        overflow: "hidden",
                        transition: "max-height 0.35s ease",
                      }}
                    >
                      <div
                        className="
                          px-6 pb-6
                          prose prose-slate prose-sm max-w-none
                          prose-headings:text-primary prose-headings:font-black prose-headings:mt-4 prose-headings:mb-2
                          prose-h3:text-sm prose-h3:uppercase prose-h3:tracking-wide
                          prose-h4:text-xs prose-h4:uppercase prose-h4:tracking-wide prose-h4:text-slate-500
                          prose-a:text-primary-700 prose-a:underline
                          prose-strong:text-slate-800 prose-strong:font-semibold
                          [&_ul]:list-none [&_ul]:p-0 [&_ul]:m-0
                          [&_ol]:list-none [&_ol]:p-0 [&_ol]:m-0
                          [&_blockquote]:border-l-4 [&_blockquote]:border-primary [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-slate-500
                          [&_table]:border-collapse [&_table]:w-full
                          [&_th]:bg-slate-50 [&_th]:text-left [&_th]:px-3 [&_th]:py-2 [&_th]:text-xs [&_th]:font-black [&_th]:uppercase [&_th]:border [&_th]:border-slate-200
                          [&_td]:px-3 [&_td]:py-2 [&_td]:border [&_td]:border-slate-200 [&_td]:text-sm
                        "
                        dangerouslySetInnerHTML={{ __html: s.html }}
                      />
                    </div>
                  </div>
                );
              })}

              {/* Footer note */}
              <div className="text-center pt-4 pb-8 space-y-2">
                <p className="text-sm text-slate-500">
                  Have questions? Please contact{" "}
                  <a
                    href="mailto:support@rentgurus.com"
                    className="font-heading text-primary font-semibold hover:underline"
                  >
                    support@rentgurus.com
                  </a>
                </p>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
